import { useState, useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PERMISSION_REGISTRY } from "@shared/permissions/registry";
import {
  LayoutDashboard, Users, FileText, Calendar, CheckSquare, Mail,
  BarChart3, Shield, CreditCard, Globe, Database, Lock, Sparkles,
  Copy, Check,
} from "lucide-react";

interface TierPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: {
    id: string;
    name: string;
    displayName: string | null;
    permissionsJson: string;
    previewBadgeText?: string | null;
    previewBadgeColor?: string | null;
    previewAccentColor?: string | null;
    previewLockedBehavior?: string | null;
    previewUpgradeCopy?: string | null;
    previewWelcomeMessage?: string | null;
    designNotes?: string | null;
  } | null;
}

type PermissionVisibility = "allow" | "deny" | "hide";

const MODULE_META: Record<string, { label: string; icon: typeof LayoutDashboard }> = {
  dashboard: { label: "Dashboard", icon: LayoutDashboard },
  leads: { label: "Leads", icon: Users },
  clients: { label: "Clients", icon: Users },
  invoices: { label: "Invoices", icon: FileText },
  schedule: { label: "Schedule", icon: Calendar },
  approvals: { label: "Approvals", icon: CheckSquare },
  communications: { label: "Communications", icon: Mail },
  reports: { label: "Reports", icon: BarChart3 },
  admin: { label: "Admin Panel", icon: Shield },
  billing: { label: "Billing", icon: CreditCard },
  platform: { label: "Platform", icon: Globe },
};

const BADGE_COLORS = [
  { value: "slate", label: "Slate", className: "bg-slate-500 text-white" },
  { value: "blue", label: "Blue", className: "bg-blue-500 text-white" },
  { value: "green", label: "Green", className: "bg-green-500 text-white" },
  { value: "amber", label: "Amber", className: "bg-amber-500 text-white" },
  { value: "red", label: "Red", className: "bg-red-500 text-white" },
  { value: "purple", label: "Purple", className: "bg-purple-500 text-white" },
  { value: "pink", label: "Pink", className: "bg-pink-500 text-white" },
];

const ACCENT_COLORS = [
  { value: "blue", label: "Blue", swatch: "bg-blue-500" },
  { value: "indigo", label: "Indigo", swatch: "bg-indigo-500" },
  { value: "violet", label: "Violet", swatch: "bg-violet-500" },
  { value: "teal", label: "Teal", swatch: "bg-teal-500" },
  { value: "emerald", label: "Emerald", swatch: "bg-emerald-500" },
  { value: "amber", label: "Amber", swatch: "bg-amber-500" },
  { value: "red", label: "Red", swatch: "bg-red-500" },
];

function badgeClass(color: string | null | undefined): string {
  return BADGE_COLORS.find(c => c.value === color)?.className || "bg-slate-500 text-white";
}

function accentSwatchClass(color: string | null | undefined): string {
  return ACCENT_COLORS.find(c => c.value === color)?.swatch || "bg-blue-500";
}

export function TierPreviewModal({ open, onOpenChange, tier }: TierPreviewModalProps) {
  const { toast } = useToast();
  const [permissions, setPermissions] = useState<Record<string, PermissionVisibility>>({});
  const [badgeText, setBadgeText] = useState("");
  const [badgeColor, setBadgeColor] = useState("slate");
  const [accentColor, setAccentColor] = useState("blue");
  const [lockedBehavior, setLockedBehavior] = useState<"hide" | "show_locked">("show_locked");
  const [upgradeCopy, setUpgradeCopy] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [designNotes, setDesignNotes] = useState("");
  const [copiedNotes, setCopiedNotes] = useState(false);

  // Hydrate state from the tier whenever the modal opens or tier changes
  useEffect(() => {
    if (!tier || !open) return;
    try {
      setPermissions(tier.permissionsJson ? JSON.parse(tier.permissionsJson) : {});
    } catch {
      setPermissions({});
    }
    setBadgeText(tier.previewBadgeText || "");
    setBadgeColor(tier.previewBadgeColor || "slate");
    setAccentColor(tier.previewAccentColor || "blue");
    setLockedBehavior((tier.previewLockedBehavior as "hide" | "show_locked") || "show_locked");
    setUpgradeCopy(tier.previewUpgradeCopy || "");
    setWelcomeMessage(tier.previewWelcomeMessage || "");
    setDesignNotes(tier.designNotes || "");
  }, [tier, open]);

  // Module list derived from PERMISSION_REGISTRY
  const modules = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const p of PERMISSION_REGISTRY) {
      if (!seen.has(p.module)) {
        seen.add(p.module);
        list.push(p.module);
      }
    }
    return list;
  }, []);

  // For the preview, treat a module as "hidden" when *every* permission in
  // it is set to "hide", and "locked" when at least one is "deny" or "hide".
  const moduleStateMap = useMemo(() => {
    const map: Record<string, "visible" | "locked" | "hidden"> = {};
    for (const m of modules) {
      const perms = PERMISSION_REGISTRY.filter(p => p.module === m);
      if (perms.length === 0) { map[m] = "visible"; continue; }
      const allHidden = perms.every(p => permissions[p.key] === "hide");
      const anyRestricted = perms.some(p => permissions[p.key] === "hide" || permissions[p.key] === "deny");
      map[m] = allHidden ? "hidden" : anyRestricted ? "locked" : "visible";
    }
    return map;
  }, [permissions, modules]);

  // Quick toggle: flip a whole module between visible (allow) and hidden
  const toggleModuleVisibility = (module: string, makeVisible: boolean) => {
    const perms = PERMISSION_REGISTRY.filter(p => p.module === module);
    setPermissions(prev => {
      const next = { ...prev };
      for (const p of perms) {
        if (makeVisible) delete next[p.key];
        else next[p.key] = "hide";
      }
      return next;
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!tier) return;
      return apiRequest("PATCH", `/api/platform/tiers/${tier.id}`, {
        permissionsJson: JSON.stringify(permissions),
        previewBadgeText: badgeText.trim() || null,
        previewBadgeColor: badgeColor || null,
        previewAccentColor: accentColor || null,
        previewLockedBehavior: lockedBehavior || null,
        previewUpgradeCopy: upgradeCopy.trim() || null,
        previewWelcomeMessage: welcomeMessage.trim() || null,
        designNotes: designNotes.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/tiers"] });
      toast({ title: "Tier preview saved", description: "Changes captured for this tier only." });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({ title: "Failed to save", description: error?.message || "Please try again", variant: "destructive" });
    },
  });

  const handleCopyNotes = async () => {
    if (!tier) return;
    const tierLabel = tier.displayName || tier.name;
    const visibleModules = modules.filter(m => moduleStateMap[m] === "visible").map(m => MODULE_META[m]?.label || m);
    const hiddenModules = modules.filter(m => moduleStateMap[m] === "hidden").map(m => MODULE_META[m]?.label || m);
    const lockedModules = modules.filter(m => moduleStateMap[m] === "locked").map(m => MODULE_META[m]?.label || m);
    const summary = [
      `[${tierLabel} tier — UI changes requested]`,
      badgeText ? `- Badge: "${badgeText}" (${badgeColor})` : "",
      `- Accent color: ${accentColor}`,
      `- Locked-feature behavior: ${lockedBehavior}`,
      welcomeMessage ? `- Welcome message: "${welcomeMessage}"` : "",
      upgradeCopy ? `- Upgrade prompt copy: "${upgradeCopy}"` : "",
      visibleModules.length ? `- Visible modules: ${visibleModules.join(", ")}` : "",
      lockedModules.length ? `- Locked modules: ${lockedModules.join(", ")}` : "",
      hiddenModules.length ? `- Hidden modules: ${hiddenModules.join(", ")}` : "",
      designNotes.trim() ? `- Notes: ${designNotes.trim()}` : "",
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(summary);
      setCopiedNotes(true);
      setTimeout(() => setCopiedNotes(false), 2000);
      toast({ title: "Copied to clipboard", description: "Paste into the agent chat to apply." });
    } catch {
      toast({ title: "Copy failed", description: "Select and copy the notes manually.", variant: "destructive" });
    }
  };

  if (!tier) return null;
  const tierLabel = tier.displayName || tier.name;
  const accentSwatch = accentSwatchClass(accentColor);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="capitalize flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {tierLabel} Tier Preview
            {badgeText && (
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${badgeClass(badgeColor)}`}>
                {badgeText.toUpperCase()}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Changes here only affect the {tierLabel} tier. Visual edits save instantly; structural changes (new components, layout) get captured in Design Notes for the next agent pass.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
          {/* LEFT — LIVE MOCK PREVIEW */}
          <div className="border-r bg-muted/30 overflow-auto p-4" data-testid="tier-preview-panel">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">
              Live Preview · what {tierLabel} workspaces see
            </div>

            {/* Mock app shell */}
            <div className="bg-background border rounded-lg shadow-sm overflow-hidden">
              {/* Mock topbar */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded ${accentSwatch}`} />
                  <span className="font-semibold text-sm">Clarik</span>
                  {badgeText && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${badgeClass(badgeColor)}`} data-testid="preview-badge">
                      {badgeText.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-muted" />
                </div>
              </div>

              <div className="flex min-h-[420px]">
                {/* Mock sidebar */}
                <div className="w-44 border-r bg-card/50 p-2 space-y-0.5">
                  {modules.map(m => {
                    const state = moduleStateMap[m];
                    if (state === "hidden" && lockedBehavior === "hide") return null;
                    const Icon = MODULE_META[m]?.icon || Database;
                    const isLocked = state !== "visible";
                    return (
                      <div
                        key={m}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                          isLocked ? "text-muted-foreground/60" : "text-foreground"
                        }`}
                        data-testid={`preview-sidebar-${m}`}
                      >
                        <Icon className="w-3.5 h-3.5 shrink-0" />
                        <span className="flex-1 truncate">{MODULE_META[m]?.label || m}</span>
                        {isLocked && <Lock className="w-3 h-3 shrink-0 opacity-60" />}
                      </div>
                    );
                  })}
                </div>

                {/* Mock content */}
                <div className="flex-1 p-5 space-y-3">
                  <div>
                    <h3 className="text-base font-semibold" data-testid="preview-welcome">
                      {welcomeMessage || `Welcome back!`}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Here's what's happening in your workspace.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="border rounded p-2 bg-card">
                        <div className={`w-2 h-2 rounded-full ${accentSwatch} mb-1.5`} />
                        <div className="h-2 bg-muted rounded w-3/4 mb-1" />
                        <div className="h-1.5 bg-muted rounded w-1/2" />
                      </div>
                    ))}
                  </div>

                  {upgradeCopy && (
                    <div className={`mt-3 p-3 rounded border-l-4 text-xs ${accentSwatch.replace("bg-", "border-l-")} bg-muted/40`} data-testid="preview-upgrade-copy">
                      <div className="font-semibold mb-1">Upgrade your plan</div>
                      <div className="text-muted-foreground">{upgradeCopy}</div>
                    </div>
                  )}

                  {modules.some(m => moduleStateMap[m] === "locked") && lockedBehavior === "show_locked" && (
                    <div className="mt-2 text-[10px] text-muted-foreground italic">
                      Locked items appear in sidebar with a lock icon.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground mt-3 italic">
              This is a visual mock. Save to apply badge/copy/visibility to real {tierLabel} workspaces. Layout/component changes live in Design Notes below.
            </p>
          </div>

          {/* RIGHT — CONFIG PANEL */}
          <ScrollArea className="overflow-auto">
            <div className="p-5 space-y-6">

              {/* Branding */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> Branding
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="badge-text" className="text-xs">Badge Text</Label>
                    <Input
                      id="badge-text"
                      value={badgeText}
                      onChange={e => setBadgeText(e.target.value)}
                      placeholder="e.g. FREE, PRO"
                      maxLength={20}
                      data-testid="input-badge-text"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="badge-color" className="text-xs">Badge Color</Label>
                    <Select value={badgeColor} onValueChange={setBadgeColor}>
                      <SelectTrigger id="badge-color" data-testid="select-badge-color">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BADGE_COLORS.map(c => (
                          <SelectItem key={c.value} value={c.value}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded ${c.className}`} />
                              {c.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="accent-color" className="text-xs">Accent Color</Label>
                  <Select value={accentColor} onValueChange={setAccentColor}>
                    <SelectTrigger id="accent-color" data-testid="select-accent-color">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCENT_COLORS.map(c => (
                        <SelectItem key={c.value} value={c.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded ${c.swatch}`} />
                            {c.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </section>

              {/* Module visibility — quick toggles */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Lock className="h-4 w-4" /> Module Visibility
                  </h3>
                  <span className="text-[11px] text-muted-foreground">Quick toggle hides all permissions in a module</span>
                </div>
                <div className="grid grid-cols-2 gap-1.5 border rounded-md p-2">
                  {modules.map(m => {
                    const state = moduleStateMap[m];
                    const Icon = MODULE_META[m]?.icon || Database;
                    return (
                      <label
                        key={m}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm"
                        data-testid={`toggle-module-${m}`}
                      >
                        <Checkbox
                          checked={state !== "hidden"}
                          onCheckedChange={(v) => toggleModuleVisibility(m, !!v)}
                        />
                        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="flex-1">{MODULE_META[m]?.label || m}</span>
                        {state === "locked" && (
                          <Badge variant="outline" className="text-[9px] py-0 px-1 h-4">partial</Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground italic">
                  For per-permission granularity (allow / deny / hide), use the existing "Configure" button on the tier card.
                </p>
              </section>

              {/* Behavior */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold">Locked Feature Behavior</h3>
                <Select value={lockedBehavior} onValueChange={(v) => setLockedBehavior(v as "hide" | "show_locked")}>
                  <SelectTrigger data-testid="select-locked-behavior">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="show_locked">Show in sidebar with lock icon</SelectItem>
                    <SelectItem value="hide">Hide entirely</SelectItem>
                  </SelectContent>
                </Select>
              </section>

              {/* Copy */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold">Copy</h3>
                <div className="space-y-1.5">
                  <Label htmlFor="welcome-msg" className="text-xs">Welcome Message</Label>
                  <Input
                    id="welcome-msg"
                    value={welcomeMessage}
                    onChange={e => setWelcomeMessage(e.target.value)}
                    placeholder="Welcome back!"
                    data-testid="input-welcome-message"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="upgrade-copy" className="text-xs">Upgrade Prompt</Label>
                  <Textarea
                    id="upgrade-copy"
                    value={upgradeCopy}
                    onChange={e => setUpgradeCopy(e.target.value)}
                    placeholder="Unlock more leads, campaigns, and team seats…"
                    rows={2}
                    data-testid="input-upgrade-copy"
                  />
                </div>
              </section>

              {/* Design notes */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Design Notes for Agent</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyNotes}
                    data-testid="button-copy-design-notes"
                  >
                    {copiedNotes ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                    {copiedNotes ? "Copied!" : "Copy as brief"}
                  </Button>
                </div>
                <Textarea
                  value={designNotes}
                  onChange={e => setDesignNotes(e.target.value)}
                  placeholder={`Anything that requires real code changes — e.g. "Free tier dashboard should hide the Revenue widget", "Move the upgrade banner to the top", "Add a comparison table on empty Reports page"…`}
                  rows={6}
                  className="text-sm font-mono"
                  data-testid="input-design-notes"
                />
                <p className="text-[11px] text-muted-foreground italic">
                  Click "Copy as brief" to get a structured summary to paste into the agent chat. Notes persist per tier so nothing gets lost between sessions.
                </p>
              </section>
            </div>
          </ScrollArea>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t bg-card">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-tier-preview">
            Cancel
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            data-testid="button-save-tier-preview"
          >
            {saveMutation.isPending ? "Saving…" : "Save Tier Preview"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
